import { Box, Typography, Button, CircularProgress, Collapse } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import { Toast } from 'primereact/toast';
import CustomAxios from "../../utils/CustomAxios";

/**
 * The test dashboard.
 *
 * Shows what `manage.py run_test_suites` last wrote, and — where the
 * environment allows it — starts a new run. The command is the product; this is
 * a view onto its output, which is why the page is useful even where the Run
 * button is not available.
 *
 * A run cannot be synchronous: the backend suite alone is 20-30 seconds and the
 * mobile one longer, so the request would time out at the proxy well before the
 * tests finished. Starting returns immediately and the page polls.
 */

const PINK = '#ED1E79';
const INK = '#16121A';
const MUTED = '#6E6478';
const LINE = '#E9E3EE';
const CHIP = '#F4EFF7';

/**
 * A status is not just pass or fail.
 *
 * `suite_error` earns its own colour because it means something different and
 * actionable: every test that ran passed, but a file could not be loaded. Shown
 * as a failure it sends people hunting for a regression that is not there.
 */
const STATUS = {
    passed: { colour: '#0B7A5E', label: 'passed' },
    failed: { colour: '#C0392B', label: 'failed' },
    suite_error: { colour: '#B7791F', label: 'a file would not load' },
    timeout: { colour: '#C0392B', label: 'timed out' },
    error: { colour: '#C0392B', label: 'could not run' },
    skipped: { colour: '#9CA3AF', label: 'skipped' },
};

export function TestDashboard() {
    const toast = useRef(null);
    const pollRef = useRef(null);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [starting, setStarting] = useState(false);
    const [open, setOpen] = useState({});
    const [downloading, setDownloading] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await CustomAxios.get('testing/results/');
            setData(res?.data || null);
            return res?.data;
        } catch (e) {
            const detail = e?.response?.data?.error || 'Could not read the test results.';
            toast.current?.show({ severity: 'error', summary: 'Failed', detail, life: 5000 });
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Poll only while something is running. A dashboard that polls forever is a
    // request every few seconds for as long as the tab is open.
    useEffect(() => {
        if (!data?.running) {
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
            return undefined;
        }
        pollRef.current = setInterval(load, 3000);
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, [data?.running, load]);

    /**
     * Download the run as a spreadsheet.
     *
     * `responseType: 'blob'` is not optional. The shared axios instance parses
     * every response as JSON by default, which turns a 30KB xlsx into a
     * mangled string and produces a file Excel refuses to open -- the same
     * failure the user reports export hit.
     */
    const downloadReport = async () => {
        setDownloading(true);
        try {
            const res = await CustomAxios.get('testing/report/', {
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = 'shino_test_report.xlsx';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.current?.show({
                severity: 'error', summary: 'Download failed',
                detail: 'Could not build the report. Run the suites first.',
                life: 5000,
            });
        } finally {
            setDownloading(false);
        }
    };

    const start = async (only) => {
        setStarting(true);
        try {
            await CustomAxios.post('testing/results/', only ? { only } : {});
            await load();
            toast.current?.show({
                severity: 'info', summary: 'Running',
                detail: 'Tests started. This page will update as suites finish.', life: 4000,
            });
        } catch (e) {
            const detail = e?.response?.data?.error || 'Could not start a run.';
            toast.current?.show({ severity: 'warn', summary: 'Not started', detail, life: 6000 });
        } finally {
            setStarting(false);
        }
    };

    const results = data?.results;
    const suites = results?.suites || [];
    const totals = results?.totals || {};
    const busy = !!data?.running;

    return (
        <Box sx={{ padding: { xs: "12px", md: "22px" }, display: "flex", flexDirection: "column", gap: "16px" }}>
            <Toast ref={toast} />

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: "12px", flexWrap: "wrap" }}>
                <Box sx={{ flex: 1, minWidth: 240 }}>
                    <Typography sx={{ fontSize: "21px", fontWeight: 750, letterSpacing: "-0.02em", color: INK }}>
                        Test automation
                    </Typography>
                    <Typography sx={{ fontSize: "13px", color: MUTED, marginTop: "2px" }}>
                        {results
                            ? `Last run ${results.finished_at} · took ${results.seconds}s`
                            : 'No run recorded yet.'}
                    </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: "9px", alignItems: "center", flexWrap: "wrap" }}>
                    <Button size="small" startIcon={<RefreshIcon />} onClick={load}
                        sx={{ textTransform: "none", fontSize: 12.5, color: MUTED }}>
                        Refresh
                    </Button>
                    {results ? (
                        <Button size="small" startIcon={<DownloadIcon />}
                            disabled={downloading} onClick={downloadReport}
                            sx={{ textTransform: "none", fontSize: 12.5, color: MUTED }}>
                            {downloading ? 'Building…' : 'Excel'}
                        </Button>
                    ) : null}
                    {data?.can_run ? (
                        <>
                            <Button variant="outlined" size="small" disabled={busy || starting}
                                onClick={() => start(['backend'])}
                                sx={{ textTransform: "none", fontSize: 12.5, borderColor: PINK, color: PINK }}>
                                Backend only
                            </Button>
                            <Button variant="contained" size="small" disabled={busy || starting}
                                startIcon={busy ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <PlayArrowIcon />}
                                onClick={() => start(null)}
                                sx={{
                                    textTransform: "none", fontSize: 12.5, fontWeight: 700,
                                    backgroundColor: INK, borderRadius: "9px",
                                    "&:hover": { backgroundColor: "#000" },
                                }}>
                                {busy ? `Running ${data.running_suite || ''}…` : 'Run all suites'}
                            </Button>
                        </>
                    ) : null}
                </Box>
            </Box>

            {data && !data.can_run ? (
                <Box sx={{
                    borderLeft: `3px solid #B7791F`, backgroundColor: '#FDF6E7',
                    borderRadius: "0 10px 10px 0", padding: "11px 14px", fontSize: 13, color: INK,
                }}>
                    {data.why_not}
                </Box>
            ) : null}

            {/* Totals across every suite, so "is it green" needs no arithmetic. */}
            {results ? (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "11px" }}>
                    {[
                        ['Passed', totals.passed ?? 0, '#0B7A5E'],
                        ['Failed', totals.failed ?? 0, (totals.failed ? '#C0392B' : '#9CA3AF')],
                        ['Skipped', totals.skipped ?? 0, '#9CA3AF'],
                        ['Suites', suites.length, '#7A4FE0'],
                    ].map(([label, value, colour]) => (
                        <Box key={label} sx={{
                            position: "relative", overflow: "hidden", backgroundColor: "#fff",
                            border: `1px solid ${LINE}`, borderRadius: "13px", padding: "12px 14px 11px 16px",
                        }}>
                            <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "3px", backgroundColor: colour }} />
                            <Typography sx={{ fontSize: "10.5px", letterSpacing: ".08em", textTransform: "uppercase", color: MUTED, fontWeight: 700 }}>
                                {label}
                            </Typography>
                            <Typography sx={{ fontSize: "24px", fontWeight: 800, fontVariantNumeric: "tabular-nums", color: INK, lineHeight: 1.2 }}>
                                {Number(value).toLocaleString('en-IN')}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            ) : null}

            <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "14px", overflow: "hidden", backgroundColor: "#fff" }}>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", padding: "44px" }}>
                        <CircularProgress size={26} sx={{ color: PINK }} />
                    </Box>
                ) : !suites.length ? (
                    <Box sx={{ padding: "38px", textAlign: "center", color: MUTED, fontSize: 13 }}>
                        Nothing recorded yet. {data?.can_run
                            ? 'Press “Run all suites”.'
                            : 'Run `python manage.py run_test_suites` on the server.'}
                    </Box>
                ) : suites.map((s) => {
                    const meta = STATUS[s.status] || STATUS.error;
                    const c = s.counts || {};
                    const total = c.total || (c.passed || 0) + (c.failed || 0);
                    const pct = total ? ((c.passed || 0) / total) * 100 : 0;
                    const expanded = !!open[s.name];
                    // Only failures are worth expanding. A skipped suite's reason is already
                    // on the row, and repeating it inside a collapse meant the same sentence
                    // appeared twice in the DOM -- confusing to read and ambiguous to test.
                    const hasDetail = (s.failures || []).length > 0;

                    return (
                        <Box key={s.name} sx={{ borderBottom: `1px solid ${LINE}` }}>
                            <Box
                                onClick={hasDetail ? () => setOpen((o) => ({ ...o, [s.name]: !o[s.name] })) : undefined}
                                sx={{
                                    display: "flex", alignItems: "center", gap: "12px", padding: "12px 15px",
                                    cursor: hasDetail ? "pointer" : "default",
                                    "&:hover": hasDetail ? { backgroundColor: CHIP } : {},
                                }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: "50%", flex: "0 0 10px", backgroundColor: meta.colour }} />
                                <Box sx={{ minWidth: 190 }}>
                                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK }}>{s.title || s.name}</Typography>
                                    <Typography sx={{ fontSize: 11.5, color: meta.colour, fontWeight: 600 }}>{meta.label}</Typography>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 70, height: 7, borderRadius: "999px", backgroundColor: CHIP, overflow: "hidden" }}>
                                    <Box sx={{ width: `${pct}%`, height: "100%", backgroundColor: meta.colour }} />
                                </Box>
                                <Typography sx={{ fontSize: 12, color: MUTED, minWidth: 200, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                                    {s.status === 'skipped'
                                        ? s.reason
                                        : `${c.passed ?? 0} passed${c.failed ? ` · ${c.failed} failed` : ''}` +
                                          `${c.skipped ? ` · ${c.skipped} skipped` : ''} · ${s.seconds}s`}
                                </Typography>
                                {hasDetail ? (
                                    <ExpandMoreIcon sx={{
                                        fontSize: 20, color: MUTED,
                                        transform: expanded ? "rotate(180deg)" : "none", transition: "transform .2s",
                                    }} />
                                ) : <Box sx={{ width: 20 }} />}
                            </Box>

                            <Collapse in={expanded}>
                                <Box sx={{ padding: "4px 15px 14px 37px", backgroundColor: "#FCFAFD" }}>
                                    {(s.failures || []).map((f, i) => (
                                        <Typography key={i} sx={{
                                            fontSize: 12.5, color: INK, padding: "4px 0",
                                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                        }}>
                                            <Box component="span" sx={{ color: meta.colour, fontWeight: 800, marginRight: "8px" }}>✕</Box>
                                            {f}
                                        </Typography>
                                    ))}
                
                                </Box>
                            </Collapse>
                        </Box>
                    );
                })}
            </Box>

            <Typography sx={{ fontSize: 12, color: MUTED }}>
                Suites run as separate processes with their own timeouts, so one hanging runner
                cannot take the others with it. Anything not installed — node_modules, a phone for
                the device suite — is reported as skipped rather than failed.
            </Typography>
        </Box>
    );
}

export default TestDashboard;
